import axios, { AxiosResponse } from 'axios';
import { config } from 'config/config';
import routePaths, { publicPaths } from 'config/routes';
import { serialize } from 'cookie';
import Router from 'next/router';
import { AnyAction, Dispatch } from 'redux';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import redirectTo from 'services/redirectTo';
import {
  fetchAppActions,
  fetchClientUserMenu,
  fetchServerAppActions,
  fetchServerUserMenu,
  resetUserHome,
  resetUserMenu,
} from 'src/actions/appActions';
import {
  getClientSideAuthInfo,
  getServerSideAuthInfo,
} from 'src/actions/auth.actions.utils';
import { getPublicMenus } from 'src/components/menu/Menu.utils';
import { IRootReducers } from 'src/reducers';
import { UserStatusEnum } from 'src/shared/enums';
import {
  ICustomer,
  IDermo,
  ISalesforceUser,
  IUser,
  IUserRole,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import { authTypes } from 'src/types/authTypes';
import {
  getCookie,
  getJourneyIds,
  getUserStatus,
  isConPlatform,
  removeAuthCookiesFromServer,
  removeCookieFromClient,
  setCookieFromClient,
} from 'utils';
import { ReduxPageContext } from '../store';
import { openToast } from 'src/components/general/Toast';
import { IMyCouponsItem } from 'src/components/account/account.interface';
import { getCouponsItemListOrdered } from 'src/pages/account/my-bonus';
import { ParsedUrlQueryInput } from 'querystring';

const {
  API: { BASE_URL },
  APP: { CURRENT_COUNTRY, ENV: APP_ENV },
  COOKIES: {
    TOAST_COOKIE,
    ACCESS_TOKEN,
    BASE_COOKIE_DOMAIN,
    REFRESH_TOKEN,
    COUNTRY_SELECTED,
  },
} = config;

const {
  INDEX,
  PAGES: { LOGIN, EXPERIENCE },
} = routePaths;

/*-------------- AUTH INTERFACES --------------- */

interface IAuthorizeUser {
  type: authTypes.AUTH_USER_LOGGED;
  payload: {
    accessToken: string;
    refreshToken: string;
    user: IUser;
    userPoints: number;
    userStatus: UserStatusEnum;
  };
}

interface IUserLogout {
  type: authTypes.AUTH_LOGOUT;
}

interface IUserDeclined {
  type: authTypes.AUTH_USER_DECLINED;
  payload: {
    userStatus: UserStatusEnum;
  };
}

interface IImpersonate {
  type: authTypes.AUTH_IMPERSONATE;
  payload: {
    accessToken: string;
    refreshToken: string;
    impersonate: boolean;
    user: IUser;
    userPoints: number;
    userStatus: UserStatusEnum;
  };
}

interface SetUserDataUpdate {
  type: authTypes.AUTH_UPDATE_USER_DATA;
  payload: { user: IUser };
}

interface SetTokens {
  type: authTypes.AUTH_SET_TOKENS;
  payload: { accessToken: string; refreshToken: string };
}

interface SetInitialUserInfo {
  type: authTypes.AUTH_SET_INITIAL_USER;
  payload: {
    user: IUser;
    userPoints: number;
    accessToken?: string;
    refreshToken?: string;
    userStatus: UserStatusEnum;
  };
}

interface SetMCSubscription {
  type: authTypes.AUTH_MC_SUBSCRIPTION;
  payload: { mcSubscriptionStatus: number; mcSegmentedSubscription: number };
}

interface SetNewsletterSubscription {
  type: authTypes.AUTH_UPDATE_NEWSLETTER_SUBSCRIPTION;
  payload: { newsletterSubscription: boolean };
}

interface ResetUserStatus {
  type: authTypes.AUTH_RESET_USER_STATUS;
  payload: {
    userStatus: null;
    accountStatus: null;
  };
}

export interface UpdateUserPoints {
  type: authTypes.AUTH_UPDATE_USER_POINTS;
  payload: { userPoints: number };
}

export interface UpdateUserCoupons {
  type: authTypes.AUTH_UPDATE_USER_COUPONS;
  payload: { userCoupons: IMyCouponsItem[] };
}

interface SetAccountStatus {
  type: authTypes.AUTH_ACCOUNT_STATUS;
  payload: {
    accountStatus?: number;
    hubCustomer?: boolean;
  };
}
interface SetProfilingBannerStatus {
  type: authTypes.AUTH_SET_PROFILING_BANNER_STATUS;
  payload: {
    profilingBannerStatus?: number;
  };
}

interface SetTermsAccepted {
  type: authTypes.AUTH_SET_TERMS_ACCEPTED;
  payload: {
    termsAccepted: boolean;
  };
}

interface SetDermoOnboarding {
  type: authTypes.AUTH_SET_DERMO_ONBOARDING;
  payload: {
    dermoOnboarding?: boolean;
  };
}

interface SetIsDermoUser {
  type: authTypes.AUTH_SET_IS_DERMO_USER;
  payload: {
    idDermoRole?: number;
    isDermo?: boolean;
    dermoStatus?: number;
  };
}

interface SetAccountSubscription {
  type: authTypes.AUTH_ACCOUNT_SUBSCRIPTION;
  payload: {
    accountSubscription?: boolean;
  };
}

interface UserProfileCompleted {
  type: authTypes.AUTH_PROFILE_COMPLETED;
  payload: {
    user: IUser;
    userPoints: number;
  };
}

interface SetUserStatus {
  type: authTypes.AUTH_SET_USER_STATUS;
  payload: {
    userStatus: number;
  };
}

interface SetUserSpecialty {
  type: authTypes.AUTH_SET_USER_SPECIALTY;
  payload: {
    userSpecialty: number;
    roleSpecialty: IUserRole;
  };
}

interface SetUserRole {
  type: authTypes.AUTH_SET_USER_ROLE;
  payload: {
    roles: IUserRole;
  };
}

export type AuthActionTypes =
  | IAuthorizeUser
  | IUserDeclined
  | IImpersonate
  | IUserLogout
  | SetInitialUserInfo
  | SetTokens
  | SetUserDataUpdate
  | SetMCSubscription
  | SetNewsletterSubscription
  | ResetUserStatus
  | UpdateUserPoints
  | UpdateUserCoupons
  | SetAccountStatus
  | SetAccountSubscription
  | UserProfileCompleted
  | SetProfilingBannerStatus
  | SetUserStatus
  | SetDermoOnboarding
  | SetIsDermoUser
  | SetUserSpecialty
  | SetUserRole
  | SetTermsAccepted;

/*-------------- AUTH ACTIONS --------------- */

export const getUserInfo = async (accessToken: string) => {
  const response = await fetch(`${BASE_URL}${apiPaths.CALL.USER_INFO}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const user: IUser = response.ok ? await response.json() : null;

  return user;
};

export const login =
  ({
    email,
    password,
    rememberMe = true,
    validateTerms = false,
    redirect = true,
    assignQr,
  }: {
    email: string;
    password: string;
    rememberMe?: boolean;
    validateTerms?: boolean;
    redirect?: boolean;
    assignQr?: {
      scanId: string;
      apiKey: string;
      qrCode: string;
      scanUserId: string;
    };
  }) =>
  async (dispatch: any) => {
    try {
      const dataPath = validateTerms
        ? apiPaths.AUTH.VALIDATE_TERMS
        : apiPaths.AUTH.LOGIN;

      const response = await api.loginCall({
        dataPath,
        data: {
          email,
          password,
          rememberMe,
        },
      });

      if (response.error) {
        throw response;
      }

      const { accessToken, refreshToken } = response;

      if (APP_ENV === 'local') {
        setCookieFromClient(ACCESS_TOKEN, accessToken);
        setCookieFromClient(REFRESH_TOKEN, refreshToken);
      }

      const user: IUser = await getUserInfo(accessToken);

      const userStatus: UserStatusEnum = getUserStatus(user);

      if (!userStatus) {
        removeCookieFromClient(ACCESS_TOKEN);
        removeCookieFromClient(REFRESH_TOKEN);

        await dispatch({
          type: authTypes.AUTH_USER_DECLINED,
          payload: {
            userStatus,
          },
        });

        return false;
      }

      if (user) {
        try {
          const userPoints = user.points;
          await dispatch(fetchClientUserMenu(accessToken));
          await dispatch(fetchAppActions(accessToken));
          setCookieFromClient(
            COUNTRY_SELECTED,
            CURRENT_COUNTRY,
            null,
            false,
            '/',
          );

          await dispatch({
            type: authTypes.AUTH_USER_LOGGED,
            payload: {
              accessToken,
              refreshToken,
              user: user,
              userPoints: userPoints || 0,
              userStatus,
            },
          });

          if (!isConPlatform) {
            await dispatch(
              _updateNewsletterSubscription(
                Boolean(Number(user.salesforce?.newsletterSubscription)),
              ),
            );
          }
          if (assignQr) {
            try {
              const registerQrAxios = axios.create({ baseURL: BASE_URL });
              registerQrAxios.defaults.headers.common['Authorization'] =
                accessToken.indexOf('Bearer') < 0
                  ? `Bearer ${accessToken}`
                  : accessToken;
              registerQrAxios.defaults.headers.common['Content-Type'] =
                'application/json';

              await registerQrAxios.post(apiPaths.AUTH.ASSIGN_QR, {
                scanId: assignQr.scanId,
                apiKey: assignQr.apiKey,
                qrCode: assignQr.qrCode,
                scanUserId: assignQr.scanUserId,
              });
            } catch (error) {
              console.error(error);
            }
          }
        } catch (error) {
          logout();
          throw error;
        }
        if (redirect) {
          redirectTo(INDEX, undefined, Router);
        }
        return true;
      }
    } catch (error) {
      throw error;
    }
  };

export const logout = (ctx?: ReduxPageContext) => async (dispatch: any) => {
  if (ctx) {
    ctx.store.dispatch({
      type: appTypes.APP_FETCH_USER_HOME,
      payload: { userHome: null },
    });
    ctx.store.dispatch({
      type: appTypes.APP_FETCH_USER_MENU,
      payload: { userMenu: null },
    });
    ctx.store.dispatch({ type: authTypes.AUTH_LOGOUT, payload: {} });

    removeAuthCookiesFromServer(ctx);
    redirectTo(INDEX, ctx);
    return;
  }

  dispatch(resetUserMenu());
  dispatch(resetUserHome());
  dispatch({
    type: authTypes.AUTH_LOGOUT,
    payload: {},
  });
  removeCookieFromClient(ACCESS_TOKEN);
  removeCookieFromClient(REFRESH_TOKEN);

  redirectTo(INDEX, undefined, Router);
  return;
};

export const impersonate = async (ctx) => {
  let user = null;
  const accessToken = ctx.query.token;

  if (!accessToken) return;

  try {
    const res = await fetch(`${BASE_URL}${apiPaths.CALL.USER_INFO}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    user = await res.json();

    if (!user) return;

    const accessExpires = new Date();
    accessExpires.setDate(accessExpires.getDate() + 1);

    ctx.res.setHeader('Set-Cookie', [
      serialize(ACCESS_TOKEN, accessToken, {
        path: '/',
        expires: accessExpires,
        domain: BASE_COOKIE_DOMAIN,
      }),
      serialize(REFRESH_TOKEN, '', {
        path: '/',
        maxAge: -1,
        domain: BASE_COOKIE_DOMAIN,
      }),
    ]);

    const userPoints = user.points;
    await ctx.store.dispatch(_impersonate(accessToken, user, userPoints));
    await fetchServerUserMenu(accessToken, ctx);
    await fetchServerAppActions(accessToken, ctx);
  } catch (err) {
    return err.response;
  }

  return [user, accessToken];
};

const _impersonate =
  (accessToken: string, user: any, userPoints: any) =>
  async (dispatch: Dispatch<IImpersonate>) => {
    const userStatus: UserStatusEnum = getUserStatus(user);

    dispatch({
      type: authTypes.AUTH_IMPERSONATE,
      payload: {
        accessToken,
        refreshToken: '',
        impersonate: true,
        user,
        userPoints: userPoints || 0,
        userStatus,
      },
    });
  };

export const _setInitialUserInfo =
  ({
    user,
    userPoints,
    accessToken,
    refreshToken,
  }: {
    user: IUser;
    userPoints: any;
    accessToken?: string;
    refreshToken?: string;
  }) =>
  (dispatch: Dispatch<SetInitialUserInfo>) => {
    const userStatus: UserStatusEnum = getUserStatus(user);

    if (accessToken && refreshToken)
      dispatch({
        type: authTypes.AUTH_SET_INITIAL_USER,
        payload: {
          accessToken,
          refreshToken,
          user,
          userPoints: userPoints || 0,
          userStatus,
        },
      });
    else
      dispatch({
        type: authTypes.AUTH_SET_INITIAL_USER,
        payload: { user, userPoints: userPoints || 0, userStatus },
      });
  };

const _setTokens =
  (accessToken: string, refreshToken: string) =>
  (dispatch: Dispatch<SetTokens>) => {
    dispatch({
      type: authTypes.AUTH_SET_TOKENS,
      payload: {
        accessToken,
        refreshToken,
      },
    });
  };

// TODO: check if this is only been used in SSR
export const _reAuthenticate = async (ctx: any) => {
  const oldRefreshToken = getCookie(REFRESH_TOKEN, ctx.req);

  try {
    const url = `${BASE_URL}${apiPaths.AUTH.REFRESH}`;

    const response = await axios.post(
      url,
      { refreshToken: oldRefreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );

    const { accessToken, refreshToken } = response.data;

    if (ctx) {
      await ctx.store.dispatch(_setTokens(accessToken, refreshToken));
      setCookieFromClient(ACCESS_TOKEN, accessToken);
      setCookieFromClient(REFRESH_TOKEN, refreshToken);
    }

    return { newAccessToken: accessToken, newRefreshToken: refreshToken };
  } catch (err) {
    if (ctx) {
      await ctx.store.dispatch(logout(ctx));
      return;
    }
  }
};

export const fetchUserData = async (ctx: any) => {
  let appAuthState: { accessToken?: string; user?: IUser };

  const enabledPaths = [
    ...publicPaths,
    ...getPublicMenus(ctx.store.getState().app.publicMenu),
  ];
  const isPublicRoute = enabledPaths.includes(ctx.pathname);

  if (ctx.req) {
    //SERVER SIDE
    appAuthState = await getServerSideAuthInfo(ctx, isPublicRoute);
  } else {
    //CLIENT SIDE
    appAuthState = getClientSideAuthInfo(ctx.store.getState());
  }

  const { user, accessToken } = appAuthState || {};

  if (!accessToken && !isPublicRoute) {
    redirectTo(LOGIN, ctx);
    return;
  }

  return [user, accessToken];
};

export const getUserPointsCon =
  () =>
  async (dispatch: Dispatch<AnyAction>, getState: () => IRootReducers) => {
    const accessToken = getState().auth.accessToken;
    const pointsResponse = await fetch(
      `${BASE_URL}${apiPaths.POINTS_API.GET_CUSTOMER_POINTS}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const points = await pointsResponse.json();
    updateUserPoints(points)(dispatch);
    return points;
  };

export const resetUserStatus = () => (dispatch: Dispatch<ResetUserStatus>) => {
  dispatch({
    type: authTypes.AUTH_RESET_USER_STATUS,
    payload: { userStatus: null, accountStatus: null },
  });
};

export const _updateNewsletterSubscription =
  (newsletterSubscription: boolean) =>
  (dispatch: Dispatch<SetNewsletterSubscription>) => {
    dispatch({
      type: authTypes.AUTH_UPDATE_NEWSLETTER_SUBSCRIPTION,
      payload: {
        newsletterSubscription,
      },
    });
  };

//con service
export const updateMCSubscription =
  (mcSubscriptionStatus: number, mcSegmentedSubscription: number) =>
  (dispatch: Dispatch<SetMCSubscription>) => {
    dispatch({
      type: authTypes.AUTH_MC_SUBSCRIPTION,
      payload: {
        mcSubscriptionStatus,
        mcSegmentedSubscription,
      },
    });
  };

export const updateSalesforceData =
  ({
    userName,
    userFirstname,
    userSurname,
    userMail,
    userPhone,
  }: {
    userName: string;
    userFirstname: string;
    userSurname: string;
    userMail: string;
    userPhone: string;
  }) =>
  (dispatch: Dispatch<SetUserDataUpdate>, getState: () => IRootReducers) => {
    const newUser = JSON.parse(JSON.stringify(getState().auth.user));

    if (newUser.salesforce) {
      newUser.salesforce.userName = userName;
      newUser.salesforce.userFirstname = userFirstname;
      newUser.salesforce.userSurname = userSurname;
      newUser.salesforce.userMail = userMail;
      newUser.salesforce.userPhone = userPhone;

      dispatch({
        type: authTypes.AUTH_UPDATE_USER_DATA,
        payload: { user: newUser },
      });
    }
  };

export const updateProfile =
  (customer: ISalesforceUser, actions: number[], newPoints: number) =>
  (dispatch: Dispatch<UserProfileCompleted>, getState: () => IRootReducers) => {
    const newUser: IUser = JSON.parse(JSON.stringify(getState().auth.user));
    let userPoints = getState().auth.userPoints;
    newUser.salesforce = customer;

    if (actions) {
      newUser.actionList = actions;
    }

    if (newPoints) {
      userPoints = newPoints;
    }

    dispatch({
      type: authTypes.AUTH_PROFILE_COMPLETED,
      payload: {
        user: newUser,
        userPoints,
      },
    });
  };

export const updateMagentoData =
  (magento: ICustomer) =>
  (dispatch: Dispatch<SetUserDataUpdate>, getState: () => IRootReducers) => {
    let user = { ...getState().auth.user };
    if (magento) user = { ...user, magento };
    dispatch({
      type: authTypes.AUTH_UPDATE_USER_DATA,
      payload: { user },
    });
  };

export const updateUserDataPharmacy =
  ({ sapCustomerId }: { sapCustomerId: string }) =>
  (dispatch: Dispatch<SetUserDataUpdate>, getState: () => IRootReducers) => {
    const user = JSON.parse(JSON.stringify(getState().auth.user));

    dispatch({
      type: authTypes.AUTH_UPDATE_USER_DATA,
      payload: {
        user: {
          ...user,
          salesforce: {
            ...user.salesforce,
            sapCustomerId: sapCustomerId,
          },
        },
      },
    });
  };

export const updateUserPoints =
  (userPoints: number) => (dispatch: Dispatch<UpdateUserPoints>) => {
    dispatch({
      type: authTypes.AUTH_UPDATE_USER_POINTS,
      payload: { userPoints: userPoints || 0 },
    });
  };

export const updateUserCoupons =
  () => async (dispatch: Dispatch<UpdateUserCoupons>) => {
    try {
      const customerCoupons = await api.getAllCustomerCoupons();
      const userCoupons = getCouponsItemListOrdered(customerCoupons?.data);

      dispatch({
        type: authTypes.AUTH_UPDATE_USER_COUPONS,
        payload: { userCoupons: userCoupons || [] },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const setProfilingBannerStatus =
  (profilingBannerStatus: number) =>
  (dispatch: Dispatch<SetProfilingBannerStatus>) => {
    dispatch({
      type: authTypes.AUTH_SET_PROFILING_BANNER_STATUS,
      payload: { profilingBannerStatus },
    });
  };

export const setTermsAccepted =
  (termsAccepted: boolean) => (dispatch: Dispatch<SetTermsAccepted>) => {
    dispatch({
      type: authTypes.AUTH_SET_TERMS_ACCEPTED,
      payload: { termsAccepted },
    });
  };

export const setDermoOnboarding =
  (dermoOnboarding: boolean) => (dispatch: Dispatch<SetDermoOnboarding>) => {
    dispatch({
      type: authTypes.AUTH_SET_DERMO_ONBOARDING,
      payload: { dermoOnboarding },
    });
  };

export const setIsDermoUser =
  (dermo: IDermo) => (dispatch: Dispatch<SetIsDermoUser>) => {
    dispatch({
      type: authTypes.AUTH_SET_IS_DERMO_USER,
      payload: { ...dermo },
    });
  };

export const setAccountStatus =
  (accountStatus: number, hubCustomer: boolean) =>
  (dispatch: Dispatch<SetAccountStatus>) => {
    dispatch({
      type: authTypes.AUTH_ACCOUNT_STATUS,
      payload: { accountStatus: accountStatus ?? 0, hubCustomer },
    });
  };

export const setAccountSubscription =
  (accountSubscription: boolean) =>
  (dispatch: Dispatch<SetAccountSubscription>) => {
    dispatch({
      type: authTypes.AUTH_ACCOUNT_SUBSCRIPTION,
      payload: { accountSubscription },
    });
  };

export const setUserSpecialty =
  (userSpecialty: number, roleSpecialty: IUserRole) =>
  (dispatch: Dispatch<SetUserSpecialty>) => {
    dispatch({
      type: authTypes.AUTH_SET_USER_SPECIALTY,
      payload: { userSpecialty, roleSpecialty },
    });
  };

export const setUserRole =
  (roles: IUserRole) => (dispatch: Dispatch<SetUserRole>) => {
    dispatch({
      type: authTypes.AUTH_SET_USER_ROLE,
      payload: { roles },
    });
  };

export const consumeQrs =
  (consumedQrList, consumedQRs) => async (dispatch: Dispatch<any>) => {
    try {
      for (let i = 0; i < consumedQrList.length; i++) {
        const productId = consumedQrList[i]?.entityId;
        const {
          data: { consumable },
        } = await api.getProductLandingById(productId);

        const qr = {
          apiKey: consumedQRs[i].apiKey,
          code: consumedQRs[i].qrCode,
          scanId: consumedQRs[i].scanID,
          userId: consumedQRs[i].scanUserId,
        };

        const scanQrResponse = await api.scanQr({
          qr: qr,
          variantId: parseInt(productId),
        });

        const customerCoupons = await api.getAllCustomerCoupons();

        const dataScan = {
          fromScan: false,
          welcome: true,
          consumable: consumable,
          userConsumableCoupon: customerCoupons?.data.coupons,
          couponConsumables: customerCoupons?.data.consumables,
          newCoupon: scanQrResponse.data.newCoupon,
          qrInfo: consumedQRs[i],
        };

        await dispatch({
          type: appTypes.APP_DATA_SCAN,
          payload: {
            scanData: dataScan,
          },
        });

        if (scanQrResponse.data?.customerTransaction?.balance) {
          dispatch({
            type: authTypes.AUTH_UPDATE_USER_POINTS,
            payload: {
              userPoints: scanQrResponse.data.customerTransaction.balance,
            },
          });
        }
      }
    } catch (error) {
      logout();
      console.error('consumeQrs: ', error);
      throw error;
    }
  };

export const validateAccount =
  (
    token: string,
    message: string,
    rememberMe: boolean = false,
    dermo: boolean = false,
    pregnancy: boolean = false,
  ) =>
  async (dispatch: any) => {
    try {
      const response = await api.loginCall({
        dataPath: apiPaths.AUTH.VALIDATE_ACCOUNT,
        data: {
          token,
          rememberMe,
          dermo,
        },
      });

      const { accessToken, refreshToken, consumedQrList, consumedQRs } =
        response;

      let destinationQuery: ParsedUrlQueryInput = {};

      if (pregnancy) {
        destinationQuery.pregnancy = pregnancy;
      }

      // Add user to journeys
      if (consumedQrList?.length) {
        const qrIds: string[] = getJourneyIds(consumedQrList);
        if (qrIds?.length) {
          await Promise.all(
            qrIds.map(async (id) =>
              api.postDataCall({
                dataPath: apiPaths.CALL.ADD_JOURNEY,
                data: {
                  idProduct: id,
                },
                callConfig: {},
              }),
            ),
          );
        }
      }

      const user: IUser = await getUserInfo(accessToken);
      const userStatus: UserStatusEnum = getUserStatus(user);

      if (!userStatus) {
        removeCookieFromClient(ACCESS_TOKEN);
        removeCookieFromClient(REFRESH_TOKEN);

        await dispatch({
          type: authTypes.AUTH_USER_DECLINED,
          payload: {
            userStatus,
          },
        });
        return false;
      }

      if (user) {
        let userPoints = user.points;
        try {
          await dispatch(fetchClientUserMenu(accessToken));
          await dispatch(fetchAppActions(accessToken));

          setCookieFromClient(TOAST_COOKIE, userPoints);
          setCookieFromClient(
            COUNTRY_SELECTED,
            CURRENT_COUNTRY,
            null,
            false,
            '/',
          );

          await dispatch({
            type: authTypes.AUTH_USER_LOGGED,
            payload: {
              accessToken,
              refreshToken,
              user: user,
              userPoints: userPoints || 0,
              userStatus,
            },
          });
        } catch (error) {
          logout();
          console.error(error);
          throw error;
        }
        if (consumedQrList?.length && consumedQRs?.length) {
          await api.checkUserQrs({ ...consumedQRs[0] });
          await consumeQrs(consumedQrList, consumedQRs)(dispatch);
          redirectTo(INDEX, undefined, Router);
        } else {
          openToast(message, 'success', 6, 'puntos', 'increase_50');
          redirectTo(INDEX, undefined, Router, destinationQuery);
        }
        return true;
      }
    } catch (error) {
      console.group('error', error);
      throw error;
    }
  };

export const refreshUserInfo = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const response: AxiosResponse<IUser> = await api.getUserInfo({
      dataPath: apiPaths.CALL.USER_INFO,
      callConfig: {},
    });
    const user: IUser = response.data;
    const userPoints: number = user.points;

    _setInitialUserInfo({ user, userPoints })(dispatch);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updateProfiling =
  () =>
  async (dispatch: Dispatch<AnyAction>, getState: () => IRootReducers) => {
    try {
      const user = getState().auth.user;

      await api.postDataCall({
        dataPath: apiPaths.AUTH.VALIDATE_PROFILING,
        data: { email: user.email },
        callConfig: {},
      });

      dispatch({
        type: authTypes.AUTH_SET_USER_STATUS,
        payload: { userStatus: UserStatusEnum.ACCEPTED_MANAGER },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
